import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button, TextField, Grid, Typography, Box, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import * as Service from '../../../services/configsms.service';

// Aba "SMS" de Configurações. Catálogo multi-provider (tb_sms_provider) + config do tenant (tb_config_sms).
// Token encriptado no backend (nunca volta em claro: só haskey). Mostra a URL de callback pronta pro painel
// do provedor. NÃO usar async em arrow class field (babel 7.12.3) — métodos async normais + bind no ctor.
class SmsConfigTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      providers: [],
      smsproviderid: '',
      pricepersegment: '',
      token: '',
      haskey: false,
      callbackurl: '',
      callbackurlstatus: '',
      name: ''
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.loadAll = this.loadAll.bind(this);
    this.renderCallbackBox = this.renderCallbackBox.bind(this);
  }

  componentDidMount() {
    this.loadAll();
  }

  async loadAll() {
    const { setLoading, setMessage, intl } = this.props;
    try {
      setLoading(true);
      const provResp = await Service.getProviders();
      const cfgResp = await Service.getConfig();

      const providers = (provResp && provResp.success && Array.isArray(provResp.data)) ? provResp.data : [];
      const cfg = (cfgResp && cfgResp.success && cfgResp.data) ? cfgResp.data : {};

      this.setState({
        providers,
        smsproviderid: (cfg.smsproviderid != null ? cfg.smsproviderid : (providers.length > 0 ? providers[0].id : '')),
        pricepersegment: (cfg.pricepersegment != null ? cfg.pricepersegment : ''),
        haskey: cfg.haskey === true,
        callbackurl: cfg.callbackurl || '',
        callbackurlstatus: cfg.callbackurlstatus || '',
        name: cfg.name || '',
        token: ''
      });
    } catch (e) {
      setMessage(true, 'error', intl.formatMessage({ id: 'process.error' }));
    } finally {
      setLoading(false);
    }
  }

  async handleSubmit() {
    const { setLoading, setMessage, intl } = this.props;
    if (!this.state.smsproviderid) {
      setMessage(true, 'error', intl.formatMessage({ id: 'sms.config.provider.required' }));
      return;
    }
    const payload = {
      smsproviderid: Number(this.state.smsproviderid),
      name: this.state.name,
      pricepersegment: (this.state.pricepersegment === '' || this.state.pricepersegment == null) ? null : Number(this.state.pricepersegment)
    };
    // Token só vai quando o admin digitou uma nova chave (vazio = mantém a atual).
    if (this.state.token && this.state.token.trim().length > 0) payload.token = this.state.token.trim();

    try {
      setLoading(true);
      const resp = await Service.save(payload);
      if (resp && resp.success) {
        const cfg = resp.data || {};
        this.setState({
          haskey: cfg.haskey === true,
          callbackurl: cfg.callbackurl || this.state.callbackurl,
          callbackurlstatus: cfg.callbackurlstatus || this.state.callbackurlstatus,
          token: ''
        });
        setMessage(true, 'success', intl.formatMessage({ id: 'sms.config.saved' }));
      } else {
        const msg = (resp && resp.errors && resp.errors[0]) ? resp.errors[0] : intl.formatMessage({ id: 'process.error' });
        setMessage(true, 'error', msg);
      }
    } catch (e) {
      setMessage(true, 'error', intl.formatMessage({ id: 'process.error' }));
    } finally {
      setLoading(false);
    }
  }

  copyValue = (value) => {
    const { setMessage, intl } = this.props;
    if (!value) return;
    try {
      navigator.clipboard.writeText(value);
      setMessage(true, 'success', intl.formatMessage({ id: 'sms.config.copied' }));
    } catch (e) { /* clipboard indisponível */ }
  };

  // Box escuro com a URL + botão copiar + ajuda. Reusado pros 2 webhooks (respostas e status).
  renderCallbackBox(label, url, help) {
    const { intl } = this.props;
    if (!url) return null;
    return (
      <div style={{ marginBottom: 12 }}>
        <Typography style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 4 }}>{label}</Typography>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#1a1a2e', borderRadius: 8, padding: '8px 12px' }}>
          <Typography style={{ fontFamily: 'monospace', fontSize: 12, color: '#e0e0e0', wordBreak: 'break-all', flex: 1 }}>{url}</Typography>
          <Tooltip title={intl.formatMessage({ id: 'sms.config.copy' })} arrow>
            <IconButton size="small" onClick={() => this.copyValue(url)} style={{ color: '#8BC34A' }}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{help}</Typography>
      </div>
    );
  }

  render() {
    const { intl } = this.props;
    const { providers, smsproviderid, pricepersegment, token, haskey, callbackurl, callbackurlstatus, name } = this.state;

    return (
      <Grid container spacing={2} style={{ maxWidth: 720 }}>
        <Grid item xs={12}>
          <Typography style={{ fontSize: 18, fontWeight: 600, color: '#333' }}>
            {intl.formatMessage({ id: 'sms.settings' })}
          </Typography>
          <Typography style={{ fontSize: 13, color: '#888' }}>
            {intl.formatMessage({ id: 'sms.config.desc' })}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} fullWidth
            label={intl.formatMessage({ id: 'sms.config.provider' })}
            value={smsproviderid}
            onChange={e => this.setState({ smsproviderid: e.target.value })}
          >
            <option value="">{intl.formatMessage({ id: 'select' })}...</option>
            {providers.map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth type="number" InputLabelProps={{ shrink: true }} inputProps={{ step: '0.0001', min: '0' }}
            label={intl.formatMessage({ id: 'sms.config.price' })}
            value={pricepersegment}
            onChange={e => this.setState({ pricepersegment: e.target.value })}
            helperText={intl.formatMessage({ id: 'sms.config.price.help' })}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth type="password" autoComplete="new-password" InputLabelProps={{ shrink: true }}
            label={intl.formatMessage({ id: 'sms.config.token' })}
            value={token}
            onChange={e => this.setState({ token: e.target.value })}
            placeholder={haskey ? intl.formatMessage({ id: 'sms.config.token.keep' }) : ''}
            helperText={haskey ? intl.formatMessage({ id: 'sms.config.token.keep' }) : intl.formatMessage({ id: 'sms.config.token.help' })}
          />
        </Grid>

        {(callbackurl || callbackurlstatus) && (
          <Grid item xs={12}>
            <Typography style={{ fontSize: 13, fontWeight: 700, color: '#555', marginBottom: 8 }}>
              {intl.formatMessage({ id: 'sms.config.callbackurl' })}
            </Typography>
            {this.renderCallbackBox(intl.formatMessage({ id: 'sms.config.callbackurl.reply' }), callbackurl, intl.formatMessage({ id: 'sms.config.callbackurl.reply.help' }))}
            {this.renderCallbackBox(intl.formatMessage({ id: 'sms.config.callbackurl.status' }), callbackurlstatus, intl.formatMessage({ id: 'sms.config.callbackurl.status.help' }))}
            <Typography style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              {intl.formatMessage({ id: 'sms.config.callbackurl.help' })}
            </Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <Button variant="contained" onClick={this.handleSubmit}
            style={{ textTransform: 'none', backgroundColor: '#8BC34A' }}>
            {intl.formatMessage({ id: 'save' })}
          </Button>
        </Grid>
      </Grid>
    );
  }
}

SmsConfigTab.propTypes = {
  intl: PropTypes.object,
  setLoading: PropTypes.func,
  setMessage: PropTypes.func
};

export default SmsConfigTab;
